import { useContext, useMemo } from 'react';
import { ManageCampaignsContext } from 'contexts';
import { manageCampaignsAPI } from 'services/manageCampaigns.service';
import { useSelector } from 'react-redux';

/*
  if you need to make the range larger - min possible day width will get more narrow
  consider hiding more dates around the hovered one (Chart.jsx),
  and adjust code for the last days that may not fit
*/
const MAX_CHART_RANGE = 180;

export const useFacade = ({ timePeriod }) => {
  const isChartHidden = useSelector((state) => state.manageCampaigns.isChartHidden);
  const selectedMetrics = useSelector((state) => state.manageCampaigns.selectedMetrics);
  // get time period
  const numOfDays = timePeriod ? getNumOfDays(timePeriod.from, timePeriod.to) : 0;
  const isValidTimeRange = numOfDays < MAX_CHART_RANGE && numOfDays > 1;

  // get filtered campaigns from the table context
  const { rowData } = useContext(ManageCampaignsContext);

  // get notes
  const notes = useSelector((state) => state?.manageCampaigns?.dataChartEvents);

  // get daily data from server
  const {
    data: chartData,
    isFetching: isFetchingDataChart,
    isSuccess: isChartDataLoaded,
  } = manageCampaignsAPI.useGetGetGraphDataQuery(timePeriod, {
    skip: !timePeriod || !Object.entries(timePeriod).length || !isValidTimeRange,
  });

  // get data per day map, sums and number of days
  const { dataPerDay, totalValues } = useMemo(
    () => formatData(chartData, rowData, notes),
    [chartData, rowData, notes],
  );

  // get prev period data
  const prevTimePeriod = useMemo(
    () => getPreviousPeriod(timePeriod.from, numOfDays),
    [timePeriod.from, numOfDays],
  );
  const { data: prevChartData, isFetching: isFetchingPrevDataChart } =
    manageCampaignsAPI.useGetGetGraphDataQuery(prevTimePeriod, {
      skip: !isChartDataLoaded || !isValidTimeRange,
    });

  // get sums for prev period
  const { totalPrevValues } = useMemo(
    () => formatPrevData(prevChartData, rowData),
    [prevChartData, rowData],
  );

  // calculate difference in %
  const change = useMemo(
    () => (isFetchingPrevDataChart ? {} : getDifference(totalValues, totalPrevValues)),
    [isFetchingPrevDataChart, totalValues, totalPrevValues],
  );

  // check if data contains only 0
  const isZeroData = checkZeroData(totalValues);

  let errorMsg = '';

  if (!dataPerDay || (!isChartDataLoaded && !isFetchingDataChart) || isZeroData)
    errorMsg = 'No data';
  if (numOfDays >= MAX_CHART_RANGE)
    errorMsg = 'The range is too large. Select smaller amount of days';
  if (numOfDays < 2) errorMsg = 'Please select a wider date range to display the graph';

  return {
    isFetchingDataChart,
    isFetchingPrevDataChart,
    isError: !isChartDataLoaded && !isValidTimeRange,
    dataPerDay,
    totalValues,
    numOfDays,
    change,
    errorMsg,
    isZeroData,
    isChartHidden,
    selectedMetrics,
  };
};

function checkZeroData(obj) {
  return !Object.values(obj).some((val) => val !== 0);
}

function formatData(data, rowData, notes) {
  if (!data || !rowData) return { dataPerDay: new Map(), totalValues: {} };

  let dataPerDay = new Map();
  let totalValues = {
    installs: 0,
    impressions: 0,
    clicks: 0,
    cost: 0,
    cr: 0,
    avg_cpi: 0,
  };

  // get values for avg cpi calculation
  let totalCpiCost = 0;
  let totalCpiInstalls = 0;

  data.forEach((day) => {
    // sums obj for each day
    let dayData = {
      installs: 0,
      impressions: 0,
      clicks: 0,
      cost: 0,
      cr: 0,
      avg_cpi: 0,
      note: '',
    };

    // get values for avg cpi calculation for that day
    let dailyCpiCost = 0;
    let dailyCpiInstalls = 0;

    day.campaigns.forEach((campaign) => {
      for (const [key, metrics] of Object.entries(campaign)) {
        // check if campaign is shown in the table
        if (rowData.some((item) => item._campaignid === +key)) {
          // add all values to day data object
          dayData.installs += metrics.installs;
          dayData.impressions += metrics.impressions;
          dayData.clicks += metrics.clicks;
          dayData.cost += metrics.cost;

          // for all campaigns (metrics.is_cpi and not)
          dailyCpiCost += metrics.cost;
          dailyCpiInstalls += metrics.installs;
        }
      }
    });

    // calculate cr and avg_cpi daily average instead of sum
    dayData.avg_cpi = dailyCpiInstalls ? dailyCpiCost / dailyCpiInstalls : 0;
    totalCpiCost += dailyCpiCost;
    totalCpiInstalls += dailyCpiInstalls;

    dayData.cr = dayData.clicks
      ? (dayData.installs / dayData.clicks) * 100
      : dayData.installs
      ? 100
      : 0;

    // check if there are any notes for that day
    notes?.forEach((note) => {
      if (areDatesEqual(new Date(day.date), new Date(note.noteDate))) {
        dayData.note = note.noteText;
      }
    });

    // add the day data for the map
    dataPerDay.set(day.date, dayData);

    // add this day values to total sums object
    totalValues.installs += dayData.installs;
    totalValues.impressions += dayData.impressions;
    totalValues.clicks += dayData.clicks;
    totalValues.cost += dayData.cost;
  });

  // calculate total avg_cpi and cr
  totalValues.avg_cpi = totalCpiInstalls ? totalCpiCost / totalCpiInstalls : 0;
  totalValues.cr = totalValues.clicks
    ? (totalValues.installs / totalValues.clicks) * 100
    : totalValues.installs
    ? 100
    : 0;

  return { dataPerDay, totalValues };
}

function formatPrevData(data, rowData) {
  if (!data || !rowData) return {};

  let totalValues = {
    installs: 0,
    impressions: 0,
    clicks: 0,
    cost: 0,
    cr: 0,
    avg_cpi: 0,
  };

  let totalCpiCost = 0;
  let totalCpiInstalls = 0;

  data.forEach((day) => {
    day.campaigns.forEach((campaign) => {
      for (const [key, metrics] of Object.entries(campaign)) {
        // check if campaign is shown in the table
        if (rowData.some((item) => item._campaignid === +key)) {
          // add all values to the sum obj
          totalValues.installs += metrics.installs;
          totalValues.impressions += metrics.impressions;
          totalValues.clicks += metrics.clicks;
          totalValues.cost += metrics.cost;

          // for all campaigns (metrics.is_cpi and not)
          totalCpiCost += metrics.cost;
          totalCpiInstalls += metrics.installs;
        }
      }
    });
  });

  // get an cr and avg_cpi average for that time period
  totalValues.cr = totalValues.clicks
    ? (totalValues.installs / totalValues.clicks) * 100
    : totalValues.installs
    ? 100
    : 0;

  totalValues.avg_cpi = totalCpiInstalls ? totalCpiCost / totalCpiInstalls : 0;

  return { totalPrevValues: totalValues };
}

function getDifference(objCur, objPrev) {
  if (!objCur || !objPrev) return {};

  const change = {};

  Object.keys(objCur).forEach((key) => {
    const cur = objCur[key] && !isNaN(objCur[key]) ? +objCur[key].toFixed(2) : 0;
    const prev = objPrev[key] && !isNaN(objPrev[key]) ? +objPrev[key].toFixed(2) : 0;

    if (cur === 0) {
      change[key] = prev ? -100 : 0;
    } else if (prev === 0) {
      change[key] = cur ? 100 : 0;
    } else {
      change[key] = ((objCur[key] - objPrev[key]) / objPrev[key]) * 100;
    }
  });

  return change;
}

function getNumOfDays(from, to) {
  const fromDate = new Date(from);
  const toDate = new Date(to);

  return Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24));
}

function getPreviousPeriod(from, days) {
  const fromDate = new Date(from);

  const previousFromDate = new Date(fromDate.getTime() - days * 24 * 60 * 60 * 1000);
  const previousToDate = new Date(fromDate.getTime() - 1 * 24 * 60 * 60 * 1000);

  const formattedPreviousFrom = formatDate(previousFromDate);
  const formattedPreviousTo = formatDate(previousToDate);

  return {
    from: formattedPreviousFrom,
    to: formattedPreviousTo,
  };
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}

function areDatesEqual(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}
